/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2021 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: file-select-service.ts
 */

// This service is an interface for all the file-select implementation services.

angular.module('prism-ui.components').service('FileSelectService', function (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    Rx: JS5Object,
) {

    enum FileValidationStatus {
        NONE = 'NONE',
        RUNNING = 'RUNNING',
        PASSED = 'PASSED',
        FAILED = 'FAILED'
    }

    //========================//
    // Abstract Functionality //
    //========================//

    let fileSelectServiceImplementations: JS5Object = {};

    this.registerFileSelectServiceImplementation = (fileSourceType: string, service: JS5Object) => {
        if (_.has(fileSelectServiceImplementations, fileSourceType)) {
            console.warn(`Replacing previous FileSelectService implementation for file source type: ${fileSourceType}`);
        }
        fileSelectServiceImplementations[fileSourceType] = service;
    };

    this.getFileSelectServiceImplementation = (fileSourceType: string) => {
        return fileSelectServiceImplementations[fileSourceType];
    };


    //====================================//
    // Abstract File Select Functionality //
    //====================================//

    this.initialize = (fileSelectConfig: JS5Object) => {
        _.forEach(fileSelectServiceImplementations, (fileSelectServiceImplementation: JS5Object) => {
            fileSelectServiceImplementation.initialize(fileSelectConfig);
        });
    };

    this.promptFile = (fileSelectConfig: JS5Object, fileSourceType: string, options: any) => {
        return this.getFileSelectServiceImplementation(fileSourceType)
            .promptFile(fileSelectConfig, options);
    };

    this.onFileChanged = (fileSelectConfig: JS5Object, newFileValue: JS5Object) => {
        _.forEach(fileSelectServiceImplementations, (fileSelectServiceImplementation: JS5Object) => {
            fileSelectServiceImplementation.onFileChanged(fileSelectConfig, newFileValue);
        });
    };

    this.cleanup = (fileSelectConfig: JS5Object) => {
        _.forEach(fileSelectServiceImplementations, (fileSelectServiceImplementation: JS5Object) => {
            fileSelectServiceImplementation.cleanup(fileSelectConfig);
        });
    };


    //========================//
    // Shared Implementations //
    //========================//

    const setFileValue = (
        fileSelectConfig: JS5Object,
        fileSourceType: string,
        files: JS5Object[],
        results: JS5Object[],
        status: FileValidationStatus,
    ) => {
        let newFileValue: JS5Object = {
            type: fileSourceType,
            files: files,
            status: status,
        };
        if (results) {
            newFileValue.results = results;
        }
        fileSelectConfig.ngModelCtrl.$setViewValue(newFileValue);
        this.onFileChanged(fileSelectConfig, newFileValue);
    };

    this.startValidation = (
        fileSelectConfig: JS5Object,
        fileSourceType: string,
        files: JS5Object[],
    ) => {
        setFileValue(
            fileSelectConfig,
            fileSourceType,
            files,
            undefined,
            FileValidationStatus.RUNNING
        );
    };

    this.completeValidation = (
        fileSelectConfig: JS5Object,
        fileSourceType: string,
        files: JS5Object[],
        results: JS5Object[]
    ) => {
        setFileValue(
            fileSelectConfig,
            fileSourceType,
            files,
            results,
            FileValidationStatus.PASSED
        );
    };

    this.failValidation = (
        fileSelectConfig: JS5Object,
        fileSourceType: string,
        files: JS5Object[],
        results: JS5Object[]
    ) => {
        setFileValue(
            fileSelectConfig,
            fileSourceType,
            files,
            results,
            FileValidationStatus.FAILED
        );
    };

    // Listen to promise and call onResolved / onReject.
    // However, don't invoke if file is changed.
    // Returns a promise that completes when any of these happen:
    //     - onResolved is called
    //     - onReject is called
    //     - File is changed (aka: validation is cancelled)
    this.waitForValidation = (
        fileSelectConfig: JS5Object,
        validationPromise: JS5Promise,
        onResolvedFunc: JS5Function,
        onRejectFunc: JS5Function,
    ) => {
        let deferred = $q.defer();
        let validationObservable = Rx.Observable
            .fromPromise(validationPromise, Rx.Scheduler.immediate)
            .takeUntil(fileSelectConfig.fileReplacedObservable);
        validationObservable.subscribe(onResolvedFunc, (rejectedValue: any) => {
            onRejectFunc(rejectedValue);
            deferred.resolve();
        }, () => {
            deferred.resolve();
        });
        return deferred.promise;
    };
});
